import React, { useEffect, useState } from 'react';
import Cookies from 'js-cookie';
import { useNavigate } from 'react-router-dom';
import { fetchData, postData } from '../components/misc';

export default function AuthCookieAdmin() {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [specificSaving, setSpecificSaving] = useState(false);
    const [currentAuthCookie, setCurrentAuthCookie] = useState('');
    const [specificLogoutCount, setSpecificLogoutCount] = useState(0);
    const [specificEmail, setSpecificEmail] = useState('');
    const [status, setStatus] = useState('');
    const navigate = useNavigate();

    useEffect(() => {
        fetchData('/api/sirri_api/logout_everyone')
            .then((data) => {
                if (data && data.responsecode === 200) {
                    setCurrentAuthCookie(data.auth_cookie || 'current');
                    setSpecificLogoutCount(data.specific_logout_count || 0);
                } else {
                    setStatus('Unable to load current auth cookie status.');
                }
            })
            .catch((err) => {
                console.error('Failed to load auth cookie status', err);
                setStatus('Unable to load current auth cookie status.');
            })
            .finally(() => setLoading(false));
    }, []);

    const logoutEveryone = () => {
        if (saving || specificSaving) return;
        const confirmed = window.confirm(
            'Log everyone out now? This invalidates the current auth cookie and users must log in again.'
        );
        if (!confirmed) return;

        setSaving(true);
        setStatus('Logging everyone out...');
        postData('/api/sirri_api/logout_everyone', {})
            .then((data) => {
                if (data && data.responsecode === 200) {
                    setCurrentAuthCookie(data.auth_cookie || '');
                    setSpecificLogoutCount(0);
                    setStatus(data.message || 'Everyone has been logged out.');
                    Cookies.remove('auth');
                    navigate('/', { replace: true });
                } else {
                    setStatus((data && data.message) || 'Unable to log everyone out.');
                }
            })
            .catch((err) => {
                console.error('Failed to log everyone out', err);
                setStatus('Unable to log everyone out.');
            })
            .finally(() => setSaving(false));
    };

    const logoutSpecificEmail = () => {
        const email = specificEmail.trim().toLowerCase();
        if (saving || specificSaving || !email) return;
        const confirmed = window.confirm(`Log out ${email} only? Everyone else stays logged in.`);
        if (!confirmed) return;

        setSpecificSaving(true);
        setStatus(`Logging out ${email}...`);
        postData('/api/sirri_api/logout_everyone', { email })
            .then((data) => {
                if (data && data.responsecode === 200) {
                    setSpecificEmail('');
                    setSpecificLogoutCount((count) => count + 1);
                    setStatus(data.message || `${email} has been logged out.`);
                } else {
                    setStatus((data && data.message) || `Unable to log out ${email}.`);
                }
            })
            .catch((err) => {
                console.error('Failed to log out specific email', err);
                setStatus(`Unable to log out ${email}.`);
            })
            .finally(() => setSpecificSaving(false));
    };

    if (loading) {
        return <p className="text-white">Loading auth cookie controls...</p>;
    }

    return (
        <div className="mx-auto max-w-2xl space-y-6 rounded-lg bg-black/40 p-6 text-white">
            <div className="space-y-2">
                <h1 className="text-2xl font-bold">Logout Users</h1>
                <p>
                    Rotate auth cookie markers so browsers with old auth cookies are forced to log in again.
                </p>
                <p className="text-sm text-gray-300">
                    Current global auth marker: <span className="font-mono">{currentAuthCookie || 'current'}</span>
                </p>
                <p className="text-sm text-gray-300">
                    Specific email logout markers active: <span className="font-mono">{specificLogoutCount}</span>
                </p>
            </div>

            <section className="space-y-3 rounded border border-white/10 p-4">
                <h2 className="text-lg font-semibold">Log out one email</h2>
                <p className="text-sm text-gray-300">
                    This invalidates only that email's current auth cookie. Their next login receives a new marker.
                </p>
                <div className="flex flex-col gap-3 sm:flex-row">
                    <input
                        type="email"
                        value={specificEmail}
                        onChange={(event) => setSpecificEmail(event.target.value)}
                        placeholder="user@example.com"
                        className="min-w-0 flex-1 rounded border border-white/20 bg-black/50 px-3 py-2 text-white placeholder:text-gray-500"
                    />
                    <button
                        type="button"
                        className="rounded bg-yellow-600 px-4 py-2 font-semibold text-black disabled:opacity-50"
                        disabled={saving || specificSaving || !specificEmail.trim()}
                        onClick={logoutSpecificEmail}
                    >
                        {specificSaving ? 'Logging out...' : 'Log out email'}
                    </button>
                </div>
            </section>

            <section className="space-y-3 rounded border border-red-500/30 p-4">
                <h2 className="text-lg font-semibold">Log out everyone</h2>
                <p className="text-sm text-gray-300">
                    This invalidates every current auth cookie and clears all per-email markers.
                </p>
                <button
                    type="button"
                    className="rounded bg-red-700 px-4 py-2 font-semibold text-white disabled:opacity-50"
                    disabled={saving || specificSaving}
                    onClick={logoutEveryone}
                >
                    {saving ? 'Logging everyone out...' : 'Log everyone out'}
                </button>
            </section>

            {status ? <p className="text-sm text-yellow-200">{status}</p> : null}
        </div>
    );
}
